import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getSocket } from '@/sockets/socket';
import { showNotification } from '@/lib/notify';
import { chatKeys } from '@/hooks/useChats';
import type { Message } from '@/types/chat.types';

/**
 * Listener global montado en el shell autenticado. El backend emite
 * `message.notify` a la room del operador asignado cada vez que un CLIENTE
 * escribe (tenga o no la conversación abierta). Acá:
 *  - refrescamos la lista de conversaciones (badge/preview en vivo)
 *  - disparamos una notificación del navegador cuando la pestaña no está
 *    enfocada, para que el cajero se entere aunque esté en otra app/pestaña.
 */
export function useOperatorNotifications(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const socket = getSocket();
    const onNotify = (msg: Message) => {
      if (msg.senderType !== 'client') return;
      // Siempre refrescar la lista de chats (preview + no leídos).
      queryClient.invalidateQueries({ queryKey: chatKeys.lists() });
      // Notificar sólo si el operador no está mirando el panel.
      const focused =
        typeof document !== 'undefined' &&
        document.visibilityState === 'visible' &&
        document.hasFocus();
      if (focused) return;
      const preview = (msg.body || '').slice(0, 120) || 'Nuevo mensaje';
      showNotification('💬 Nuevo mensaje de un jugador', preview);
    };
    socket.on('message.notify', onNotify);
    return () => {
      socket.off('message.notify', onNotify);
    };
  }, [queryClient]);
}
